import { forwardRef } from 'react'
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react'

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  variant?: 'outline' | 'underline' | 'none'
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, variant = 'underline', className = '', ...props }, ref) => {
    let baseStyles = 'w-full text-right text-[#2d2d2d] bg-transparent placeholder-gray-400 focus:outline-none transition-all duration-200 font-[FbAsli] '
    
    if (variant === 'outline') {
      baseStyles += 'border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1a6fad] focus:border-transparent '
    } else if (variant === 'underline') {
      baseStyles += 'border-b border-gray-300 py-2 px-1 focus:border-[#1a6fad] '
    }

    return (
      <div className="flex flex-col gap-1 w-full">
        {label && (
          <label className="text-sm font-bold text-[#2d2d2d] text-right">{label}</label>
        )}
        <input ref={ref} className={`${baseStyles} ${error ? 'border-red-400' : ''} ${className}`} {...props} />
        {error && <span className="text-xs text-red-500 text-right">{error}</span>}
      </div>
    )
  }
)
Input.displayName = 'Input'

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string
  error?: string
  variant?: 'outline' | 'underline' | 'none'
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ label, error, variant = 'none', className = '', ...props }, ref) => {
    let baseStyles = 'w-full text-right text-[#2d2d2d] bg-transparent placeholder-gray-400 focus:outline-none transition-all duration-200 font-[FbAsli] resize-none '
    
    if (variant === 'outline') {
      baseStyles += 'border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#1a6fad] focus:border-transparent '
    } else if (variant === 'underline') {
      baseStyles += 'border-b border-gray-300 py-2 px-1 focus:border-[#1a6fad] '
    }

    return (
      <div className="flex flex-col gap-1 w-full h-full">
        {label && (
          <label className="text-sm font-bold text-[#2d2d2d] text-right">{label}</label>
        )}
        <textarea
          ref={ref}
          className={`${baseStyles} ${error ? 'border-red-400' : ''} ${className}`}
          {...props}
        />
        {error && <span className="text-xs text-red-500 text-right">{error}</span>}
      </div>
    )
  }
)
Textarea.displayName = 'Textarea'
